import React from 'react';
import { ChevronDown, User, Mail, Phone, Users, AlertCircle } from 'lucide-react';

const ProfileForm = ({ profileData, isEditing, onInputChange, validationErrors = {} }) => {
  // Gender options array
  const genderOptions = [
    "Male",
    "Female",
    "Other"
  ];

  // Handle input with real-time filtering
  const handleInputFilter = (field, value, event) => {
    let filteredValue = value;

    switch (field) {
      case 'phone':
        // Only allow numeric input, prevent non-numeric characters from being typed
        filteredValue = value.replace(/[^0-9]/g, '');
        // Prevent input if already 10 digits
        if (filteredValue.length > 10) {
          event.preventDefault();
          return;
        }
        break;

      case 'first_name':
      case 'last_name':
        // Only allow letters and spaces, remove numbers and special characters
        filteredValue = value.replace(/[^a-zA-Z\s]/g, '');
        break;

      case 'email':
        // Trim spaces for email
        filteredValue = value.trim();
        break;

      default:
        break;
    }

    onInputChange(field, filteredValue);
  };

  // Handle keypress events for additional filtering
  const handleKeyPress = (field, event) => {
    switch (field) {
      case 'phone':
        // Only allow numeric keys
        if (!/[0-9]/.test(event.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
          event.preventDefault();
        }
        break;

      case 'first_name':
      case 'last_name':
        // Only allow letters and spaces
        if (!/[a-zA-Z\s]/.test(event.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
          event.preventDefault();
        }
        break;

      default:
        break;
    }
  };

  // Error message component
  const ErrorMessage = ({ error }) => {
    if (!error) return null;

    return (
      <div className="flex items-center gap-1 mt-1 text-red-600 text-sm" role="alert">
        <AlertCircle className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
        <span>{error}</span>
      </div>
    );
  };

  return (
    <section
      className="p-6"
      aria-labelledby="profile-form-heading"
    >
      <h2 id="profile-form-heading" className="sr-only">Profile Information Form</h2>

      {/* First row - Name fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="space-y-2">
          <label
            htmlFor="first_name"
            className="flex items-center gap-2 text-base text-black opacity-80 font-medium max-sm:text-sm"
          >
            <User className="w-4 h-4 text-teal-600" aria-hidden="true" />
            First Name
          </label>
          <div className="relative">
            <input
              id="first_name"
              type="text"
              value={profileData.first_name}
              onChange={(e) => handleInputFilter('first_name', e.target.value, e)}
              onKeyDown={(e) => handleKeyPress('first_name', e)}
              placeholder="Your First Name"
              disabled={!isEditing}
              className={`w-full px-4 py-3 rounded-lg border-none outline-none text-base text-black placeholder-black placeholder-opacity-40 max-sm:text-sm transition-colors ${
                isEditing
                  ? `bg-stone-50 focus:ring-2 ${validationErrors.first_name ? 'focus:ring-red-500 bg-red-50' : 'focus:ring-teal-500'}`
                  : 'bg-gray-100 cursor-not-allowed'
              }`}
              aria-describedby={validationErrors.first_name ? "first_name-error" : "first_name-help"}
              aria-invalid={!!validationErrors.first_name}
            />
          </div>
          <ErrorMessage error={validationErrors.first_name} />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="last_name"
            className="flex items-center gap-2 text-base text-black opacity-80 font-medium max-sm:text-sm"
          >
            <User className="w-4 h-4 text-teal-600" aria-hidden="true" />
            Last Name
          </label>
          <div className="relative">
            <input
              id="last_name"
              type="text"
              value={profileData.last_name}
              onChange={(e) => handleInputFilter('last_name', e.target.value, e)}
              onKeyDown={(e) => handleKeyPress('last_name', e)}
              placeholder="Your Last Name"
              disabled={!isEditing}
              className={`w-full px-4 py-3 rounded-lg border-none outline-none text-base text-black placeholder-black placeholder-opacity-40 max-sm:text-sm transition-colors ${
                isEditing
                  ? `bg-stone-50 focus:ring-2 ${validationErrors.last_name ? 'focus:ring-red-500 bg-red-50' : 'focus:ring-teal-500'}`
                  : 'bg-gray-100 cursor-not-allowed'
              }`}
              aria-describedby={validationErrors.last_name ? "last_name-error" : "last_name-help"}
              aria-invalid={!!validationErrors.last_name}
            />
          </div>
          <ErrorMessage error={validationErrors.last_name} />
        </div>
      </div>

      {/* Second row - Email and Phone */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="space-y-2">
          <label
            htmlFor="email"
            className="flex items-center gap-2 text-base text-black opacity-80 font-medium max-sm:text-sm"
          >
            <Mail className="w-4 h-4 text-teal-600" aria-hidden="true" />
            Email Address
          </label>
          <div className="relative">
            <input
              id="email"
              type="email"
              value={profileData.email}
              onChange={(e) => handleInputFilter('email', e.target.value, e)}
              placeholder="your.email@example.com"
              disabled={!isEditing}
              className={`w-full px-4 py-3 rounded-lg border-none outline-none text-base text-black placeholder-black placeholder-opacity-40 max-sm:text-sm transition-colors ${
                isEditing
                  ? `bg-stone-50 focus:ring-2 ${validationErrors.email ? 'focus:ring-red-500 bg-red-50' : 'focus:ring-teal-500'}`
                  : 'bg-gray-100 cursor-not-allowed'
              }`}
              aria-describedby={validationErrors.email ? "email-error" : "email-help"}
              aria-invalid={!!validationErrors.email}
            />
          </div>
          <ErrorMessage error={validationErrors.email} />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="phone"
            className="flex items-center gap-2 text-base text-black opacity-80 font-medium max-sm:text-sm"
          >
            <Phone className="w-4 h-4 text-teal-600" aria-hidden="true" />
            Phone Number
          </label>
          <div className="relative">
            <input
              id="phone"
              type="tel"
              value={profileData.phone}
              onChange={(e) => handleInputFilter('phone', e.target.value, e)}
              onKeyDown={(e) => handleKeyPress('phone', e)}
              placeholder="1234567890"
              disabled={!isEditing}
              maxLength="10"
              className={`w-full px-4 py-3 rounded-lg border-none outline-none text-base text-black placeholder-black placeholder-opacity-40 max-sm:text-sm transition-colors ${
                isEditing
                  ? `bg-stone-50 focus:ring-2 ${validationErrors.phone ? 'focus:ring-red-500 bg-red-50' : 'focus:ring-teal-500'}`
                  : 'bg-gray-100 cursor-not-allowed'
              }`}
              aria-describedby={validationErrors.phone ? "phone-error" : "phone-help"}
              aria-invalid={!!validationErrors.phone}
            />
            {isEditing && (
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-xs text-gray-500">
                {profileData.phone ? profileData.phone.length : 0}/10
              </div>
            )}
          </div>
          <ErrorMessage error={validationErrors.phone} />
          {isEditing && !validationErrors.phone && (
            <div className="text-xs text-gray-500 mt-1">
              Enter 10-digit phone number (numbers only)
            </div>
          )}
        </div>
      </div>

      {/* Third row - Gender */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-1">
        <div className="space-y-2">
          <label
            htmlFor="gender"
            className="flex items-center gap-2 text-base text-black opacity-80 font-medium max-sm:text-sm"
          >
            <Users className="w-4 h-4 text-teal-600" aria-hidden="true" />
            Gender
          </label>
          <div className="relative">
            <select
              id="gender"
              value={profileData.gender || ""}
              onChange={(e) => onInputChange('gender', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-4 py-3 rounded-lg border-none outline-none text-base text-black appearance-none max-sm:text-sm transition-colors ${
                isEditing
                  ? 'bg-stone-50 focus:ring-2 focus:ring-teal-500'
                  : 'bg-gray-100 cursor-not-allowed'
              }`}
              aria-describedby="gender-help"
            >
              <option value="" className="text-black opacity-40">
                Select your Gender
              </option>
              {genderOptions.map((gender) => (
                <option key={gender} value={gender}>
                  {gender}
                </option>
              ))}
            </select>
            <ChevronDown
              className={`absolute right-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-black opacity-50 pointer-events-none ${
                !isEditing ? 'opacity-30' : ''
              }`}
              aria-hidden="true"
            />
          </div>
        </div>
        {/* Empty div to maintain grid layout */}
        <div></div>
      </div>

      {/* Validation Summary */}
      {isEditing && Object.keys(validationErrors).some(key => validationErrors[key]) && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <div className="flex items-center gap-2 text-red-800 text-sm font-medium mb-2">
            <AlertCircle className="w-4 h-4" aria-hidden="true" />
            Please fix the following errors:
          </div>
          <ul className="text-red-700 text-sm space-y-1 ml-6">
            {Object.entries(validationErrors).map(([field, error]) =>
              error && (
                <li key={field} className="list-disc">
                  {error}
                </li>
              )
            )}
          </ul>
        </div>
      )}
    </section>
  );
};

export default ProfileForm;
